const React = require('react');
const { useSelector, useDispatch } = require('react-redux');
const { useState, useMemo } = React;
const IrrigationRecommendationCard = require('./IrrigationRecommendationCard');
require('./RecommendationTileWrapper.css');
const $ = require('jquery');
const { useTranslation } = require('react-i18next');
const {
  parseDateInput,
  FromDateToStringFormat,
  getThisWeekIndex,
  getWeekType,
  weeklyPlannerModes,
} = require('@managers/planningManager');
const RecommendationFeedback = require('./RecommendationFeedback');
const NoPlanningMessage = require('./NoPlanningMessage');
const {
  update_field_recommendation_show_rate_line,
} = require('@actions/distributionActions');
const { trackRecommendationWeekEvent } = require('@managers/fieldManager');

const RecommendationTileWrapper = () => {
  const dispatch = useDispatch();

  // Get current field and recommendations from Redux
  const selectedFieldRedux = useSelector(
    (state) => state.distribution_data.selected_entities.field,
  );
  const recommendationsByFieldRedux = useSelector(
    (state) =>
      state.distribution_data.farming_unit_recommendations.recommendations,
  );

  const { t } = useTranslation();

  // Get the recommendations array for the current field
  const recArr =
    recommendationsByFieldRedux?.[selectedFieldRedux?.id]?.recommendations ||
    [];

  const showRateLineFromRedux =
    recommendationsByFieldRedux?.[selectedFieldRedux?.id]?.show_rate_line;

  const recommendationUnits =
    recommendationsByFieldRedux?.[selectedFieldRedux?.id]?.units || 'mm';

  // Transform array to object keyed by week (local_midnight_week_start)
  const recByWeek = useMemo(() => {
    return recArr.reduce((acc, rec) => {
      const weekKey = rec?.local_midnight_week_start;
      acc[weekKey] = rec;
      return acc;
    }, {});
  }, [recArr]);

  // Get all week keys for this field, sorted chronologically
  const weekKeys = useMemo(() => Object.keys(recByWeek).sort(), [recByWeek]);
  // The API returns weeks as [previous, current, next], so 'this week' is the
  // middle element — see getThisWeekIndex.
  const thisWeekIdx = getThisWeekIndex(weekKeys);

  // Local state: which week is currently shown (default: 'this week')
  const [weekIdx, setWeekIdx] = useState(thisWeekIdx);

  // Ensure weekIdx is updated when thisWeekIdx changes (e.g., new data loads)
  React.useEffect(() => {
    setWeekIdx(thisWeekIdx);
  }, [thisWeekIdx]);

  // Get the current recommendation
  const weekKey = weekKeys[weekIdx];
  const rec = recByWeek[weekKey];

  // Compute weekType for the card header based on today/selected week
  let weekType = getWeekType(weekIdx, thisWeekIdx);

  // Compute the "(start - end)" range string for a given week index
  const computeWeekRange = (idx) => {
    const startDateObj = parseDateInput(weekKeys[idx]);
    // End date is the day before the next week start, or start + 6 days if last week
    let endDateObj;
    if (idx < weekKeys.length - 1) {
      const nextStart = parseDateInput(weekKeys[idx + 1]);
      endDateObj = new Date(nextStart);
      endDateObj.setDate(endDateObj.getDate() - 1);
    } else {
      endDateObj = new Date(startDateObj);
      endDateObj.setDate(endDateObj.getDate() + 6);
    }
    return `(${FromDateToStringFormat(startDateObj)} - ${FromDateToStringFormat(endDateObj)})`;
  };

  const weekRange = computeWeekRange(weekIdx);

  // Navigate to a week and fire the Mixpanel arrow-click event for the week we
  // move TO, matching the mobile app's "Weekly Recommendation Next/Previous
  // Week Clicked". Tracking is by click direction (not by resulting weekType)
  // so the correct event name always fires. We intentionally do NOT track when
  // landing on the current week — only navigations to a past/future week.
  const goToWeek = (targetIdx, direction) => {
    if (targetIdx === weekIdx) {
      return;
    }
    if (targetIdx !== thisWeekIdx) {
      const targetRec = recByWeek[weekKeys[targetIdx]];
      trackRecommendationWeekEvent(
        direction,
        targetRec?.irrigations?.amount,
        recommendationUnits,
        computeWeekRange(targetIdx),
      );
    }
    setWeekIdx(targetIdx);
  };

  const onPrev = () => goToWeek(Math.max(0, weekIdx - 1), 'previous');
  const onNext = () =>
    goToWeek(Math.min(weekKeys.length - 1, weekIdx + 1), 'next');

  const FeedbackHandler = () => {
    // Update Redux state to hide the feedback component after thanks message
    setTimeout(() => {
      dispatch(
        update_field_recommendation_show_rate_line(
          selectedFieldRedux.id,
          false,
        ),
      );
    }, 3500);
  };

  const plannerMode =
    selectedFieldRedux?.settings?.planner_settings?.mode ||
    weeklyPlannerModes.WEEKLY;

  const cardProps = {
    weekType,
    weekRange,
    irrigate: rec?.irrigations?.amount,
    frequency: rec?.irrigations?.events,
    dailyReturn: rec?.irrigations?.daily_return,
    onPrev,
    onNext,
    isPrevDisabled: weekIdx === 0,
    isNextDisabled: weekIdx === weekKeys.length - 1,
    mode: plannerMode,
    user_length_unit: recommendationUnits,
    irrigation_unit: rec?.irrigations?.value_units,
  };

  // Tooltip IDs (unique per component instance)
  const tooltipID = 'irrigationRecInfoTooltip';
  const tooltipContainerID = 'irrigationRecInfoTooltipContainer';

  React.useEffect(() => {
    $(() => {
      $(`[data-toggle="tooltip"]`).tooltip({ offset: '0,6' });
    });
  }, []);

  // Defensive: If no field or no recommendations, show nothing
  // IMPORTANT: This check must be AFTER all hooks
  if (!selectedFieldRedux || !recommendationsByFieldRedux) {
    return null;
  }

  return (
    <div className='recommendation-tile-wrapper'>
      <div className='recommendation-tile-card'>
        <div className='header' id={tooltipContainerID}>
          <span className='cardTitle'>{t('new_planner_title')}</span>
          <img
            data-toggle='tooltip'
            id={tooltipID}
            data-container={`#${tooltipContainerID}`}
            onMouseOver={() => {
              $(`#${tooltipID}`).tooltip('show');
            }}
            data-placement='top'
            title={t('new_planner_rain_excluded')}
            className='infoIcon'
            src={require('images/icons/info_icon.svg')}
          />
        </div>
        {(() => {
          if (cardProps.irrigate === undefined) {
            return (
              <NoPlanningMessage
                weekType={weekType}
                weekRange={weekRange}
                onPrev={cardProps.onPrev}
                onNext={cardProps.onNext}
                isPrevDisabled={cardProps.isPrevDisabled}
                isNextDisabled={cardProps.isNextDisabled}
                messageType='new_planner_no_recommendation_available_indication'
              />
            );
          }
          if (cardProps.irrigate === 0) {
            return (
              <NoPlanningMessage
                weekType={weekType}
                weekRange={weekRange}
                onPrev={cardProps.onPrev}
                onNext={cardProps.onNext}
                isPrevDisabled={cardProps.isPrevDisabled}
                isNextDisabled={cardProps.isNextDisabled}
                messageType='no_irrigation_planning_this_week'
              />
            );
          }
          return <IrrigationRecommendationCard {...cardProps} />;
        })()}
        <div
          className={`feedback-row-wrapper${weekType === 'this' && showRateLineFromRedux ? ' visible' : ''}`}
        >
          <RecommendationFeedback
            onChange={FeedbackHandler}
            t={t}
            recommendationAmount={rec?.irrigations?.amount}
            units={recommendationUnits}
            weekRange={weekRange}
            recommendationMode={plannerMode}
            fieldId={selectedFieldRedux?.id}
            dateGenerated={rec?.date_generated}
            localMidnightWeekStart={weekKey}
          />
        </div>
      </div>
    </div>
  );
};

module.exports = React.memo(RecommendationTileWrapper);
